/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react'
import { Tooltip } from '@progress/kendo-react-tooltip'

import { AsyncComboBox } from '@/matteroomTab/AsyncComboBox'
import {
  searchObjects,
  formatSolrKeyword,
} from '../../../../../../api/commonApi'
import { cn } from '@utils/mergeClassNames'
import CLOSE_ICON from '@public/assets/action/close_circle.svg'
import WARNING_ICON from '@public/assets/action/warning-circle.svg'
import SEARCH_ICON from '@public/assets/action/icon_zoom.svg'
import CLIENT_ICON from '@public/assets/common/Icon_Client.svg'

const getCodeFieldRootClasses = (status, tone = 'client') =>
  cn('code-field group min-w-[104px] text-sm', tone, status)

const getCodeFieldDisplayClasses = status =>
  cn(
    'relative flex min-h-10 min-w-0 cursor-pointer items-center gap-2 rounded-md px-1 text-sm transition-colors',
    (status === 'hover' || status === 'filled') && 'bg-[#F7EFF9]',
    status === 'focus' && 'bg-[#D9ADE1]'
  )

const getCodeFieldBorderClasses = status =>
  cn(
    'h-0.5 shrink-0 self-stretch rounded-sm -translate-y-0.5',
    status === 'error'
      ? 'bg-[#B3261E]'
      : status === 'filled'
        ? 'bg-white group-hover:bg-[#D9ADE1]'
        : 'bg-[#D9ADE1]'
  )

const EditableClientField = ({
  value,
  displayValue,
  tooltipValue,
  onChange,
  tempClients,
  clientVersion,
  setClientVersion,
  setMatterVersion,
  setTempClients,
  setFormData,
  error,
  clearError,
  hasDestination,
  className = '',
}) => {
  const [status, setStatus] = useState('default')
  const anchorRef = useRef(null)
  const comboRef = useRef(null)
  const clickOutsideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )
  const statusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (error) setStatus('error')
    else if (value) setStatus('filled')
  }, [value, error])

  useEffect(() => {
    const handleClickOutside = event => {
      if (clickOutsideTimeoutRef.current) {
        clearTimeout(clickOutsideTimeoutRef.current)
      }

      clickOutsideTimeoutRef.current = setTimeout(() => {
        const dropdown = document.querySelector('.k-animation-container')
        const comboEl = comboRef.current?.element
        const ignoreClasses = ['k-i-x', 'k-input-inner', 'k-i-caret-alt-down']
        const clickInsideIgnored = ignoreClasses.some(className =>
          event.target.closest(`.${className}`)
        )

        const clickInsideCombo = comboEl?.contains(event.target)
        const clickInsideDropdown = dropdown?.contains(event.target)

        if (
          status === 'focus' &&
          !clickInsideCombo &&
          !clickInsideDropdown &&
          !clickInsideIgnored
        ) {
          setStatus(value ? 'filled' : 'default')
        }
      }, 0)
    }

    document.addEventListener('mouseup', handleClickOutside)
    return () => {
      document.removeEventListener('mouseup', handleClickOutside)
      if (clickOutsideTimeoutRef.current) {
        clearTimeout(clickOutsideTimeoutRef.current)
        clickOutsideTimeoutRef.current = null
      }
    }
  }, [status, value])

  useEffect(() => {
    return () => {
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current)
      }
    }
  }, [])

  return (
    <div
      className={cn(getCodeFieldRootClasses(status, 'client'), className)}
      onMouseEnter={() => status === 'default' && setStatus('hover')}
      onMouseLeave={() => status === 'hover' && setStatus('default')}
    >
      {status === 'focus' ? (
        <AsyncComboBox
          ref={comboRef}
          defaultOpen
          valid={error}
          id="new-client"
          key={`client-${clientVersion}`}
          localAddedOptions={tempClients}
          hasDestination={hasDestination}
          onAddNew={newClient => setTempClients(prev => [...prev, newClient])}
          value={value}
          onChange={(value, raw) => {
            if (!value) {
              setFormData(prev => ({
                ...prev,
                client_number: '',
                client_name: '',
                client_id: null,
                matter_number: '',
                matter_name: '',
                matter_id: null,
                matter_type: null,
              }))
              setClientVersion(prev => prev + 1)
            } else {
              setFormData(prev => ({
                ...prev,
                client_number: value,
                client_name: raw?.name || raw?.client_name || '',
                client_id: raw?.obj_id || null,
              }))
            }
            setMatterVersion(prev => prev + 1)
            if (!value) return
            if (statusTimeoutRef.current) {
              clearTimeout(statusTimeoutRef.current)
            }
            statusTimeoutRef.current = setTimeout(() => {
              setStatus(value ? 'filled' : 'default')
              statusTimeoutRef.current = null
            }, 100)
          }}
          fetchOptions={async query => {
            const formattedQuery = formatSolrKeyword(query)
            const filter = `(((number:*${formattedQuery}*) OR (name:*${formattedQuery}*)) AND is_active:true)`
            return searchObjects(
              'client',
              query,
              0,
              40,
              'obj_id,name,number',
              filter,
              hasDestination
            )
          }}
          config={{
            field: 'client',
            labelFields: ['number', 'name'],
            value: 'number',
            separator: ' ',
            valueLabel: 'Client Number',
            nameLabel: 'Client Name',
            typeLabel: 'Client',
          }}
          placeholder="Search client"
        />
      ) : status === 'error' ? (
        <>
          <div
            className="box-border flex h-10 cursor-pointer items-center gap-2 rounded border border-[#B3261E] px-1"
            onClick={() => {
              clearError?.('client_number')
              setStatus('focus')
            }}
          >
            <img className="h-4 w-4" src={CLIENT_ICON} alt="" />
            {/* flex-1 pushes the warning and search icons to the right edge. */}
            <span className="min-w-[60px] flex-1 text-sm font-normal text-[#999]">
              <span className="text-sm font-normal text-[#F55A4E]">*</span>
              Client
            </span>
            <img className="h-4 w-4" src={WARNING_ICON} alt="warning" />
            <img className="search" src={SEARCH_ICON} alt="" />
          </div>
          {error && <div className="error-text">{error}</div>}
        </>
      ) : (
        <>
          <div
            ref={anchorRef}
            className={getCodeFieldDisplayClasses(status, 'client')}
            onClick={() => setStatus('focus')}
          >
            <img className="h-4 w-4" src={CLIENT_ICON} alt="" />
            {!value && (
              <span className="min-w-[60px] text-sm font-normal text-[#999]">
                <span className="text-sm font-normal text-[#F55A4E]">*</span>
                Client
              </span>
            )}

            {value && (
              <Tooltip
                tooltipStyle={{
                  maxWidth: '264px',
                  border: 'none',
                  backgroundColor: '#000',
                  color: '#fff',
                  padding: '4px 8px',
                  fontSize: '14px',
                  fontWeight: 400,
                }}
                anchorElement="target"
                position="top"
              >
                <span
                  title={`${tooltipValue}`}
                  className="min-w-0 max-w-[320px] flex-1 cursor-default truncate pr-11"
                >
                  {displayValue}
                </span>
              </Tooltip>
            )}

            <div className="absolute right-1 flex shrink-0 items-center gap-1.5">
              {value && (
                <img
                  className="cursor-pointer"
                  src={CLOSE_ICON}
                  onClick={event => {
                    event.stopPropagation()
                    onChange(null)
                  }}
                  alt="clear"
                />
              )}
              <img className="search" src={SEARCH_ICON} alt="" />
            </div>
          </div>

          <div className={getCodeFieldBorderClasses(status, 'client')} />
        </>
      )}
    </div>
  )
}

export default EditableClientField
